import { createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

import { handleRejectedResponse } from './utils';

/*
 * Restock (Replenishment) module — per-user settings + the recommended-qty calculation.
 *
 * Settings (the three lead times + a default target-coverage) are persisted server-side by REUSING
 * the generic user_active_preferences store (POST/GET /users/active-preference) under a dedicated
 * context_key — exactly like the QZ printer preference (see slices/printing.js). No backend/schema
 * change is required: the values live in that row's JSON `applied_filters` column.
 *
 * Spec: docs/replenishment-module-one-pager.md
 */
const NODE = process.env.OPULENT_NODE_API_URL;
export const REPLENISHMENT_PREF_CONTEXT = 'replenishment-settings';

// Defaults until the user saves their own. Lead times start EMPTY — the user must enter them (the
// recommended-qty calc stays gated until Replenishment Settings are saved).
export const DEFAULT_REPLENISHMENT_SETTINGS = {
  vendorLeadTime: '',
  warehouseProcessingLeadTime: '',
  amazonReceivingLeadTime: '',
  defaultTargetDays: 90,
  // Which ASV window feeds the recommended-qty formula. 90 for everyone by default; switchable.
  defaultAsvWindow: 90,
  // Whether Pending-on-PO counts as current stock in the recommended-qty / surplus formulas.
  // ON by default; turn OFF to ignore draft-PO quantities everywhere the calc runs.
  includePendingOnPo: true
};

// Target-coverage windows — recommended qty is precomputed daily per user for each of these and
// stored on seller_skus (recommended_qty_7/15/30/60/90).
export const TARGET_DAYS_OPTIONS = [7, 15, 30, 60, 90];

// ASV windows the user can pick as the velocity source for recommended qty (widest first).
export const ASV_WINDOW_OPTIONS = [7, 15, 30, 60, 90];

export const fetchReplenishmentSettings = createAsyncThunk(
  'restock/get-settings',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${NODE}/users/active-preference`, {
        params: { context_key: REPLENISHMENT_PREF_CONTEXT }
      });
      return response?.data; // { success, data: { applied_filters: {...} } | null }
    } catch (err) {
      return handleRejectedResponse(err, 'Get Replenishment Settings', rejectWithValue);
    }
  }
);

export const saveReplenishmentSettings = createAsyncThunk(
  'restock/save-settings',
  async (settings, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${NODE}/users/active-preference`, {
        context_key: REPLENISHMENT_PREF_CONTEXT,
        applied_filters: settings
      });
      return response?.data;
    } catch (err) {
      return handleRejectedResponse(err, 'Save Replenishment Settings', rejectWithValue);
    }
  }
);

// Pull the settings blob out of a GET /users/active-preference response (or return defaults).
export const extractReplenishmentSettings = (payload) => {
  const ap = payload?.data?.applied_filters;
  if (!ap || typeof ap !== 'object') return { ...DEFAULT_REPLENISHMENT_SETTINGS };
  const num = (v, d) => (v === undefined || v === null || v === '' || Number.isNaN(Number(v)) ? d : Number(v));
  return {
    vendorLeadTime: num(ap.vendorLeadTime, DEFAULT_REPLENISHMENT_SETTINGS.vendorLeadTime),
    warehouseProcessingLeadTime: num(ap.warehouseProcessingLeadTime, DEFAULT_REPLENISHMENT_SETTINGS.warehouseProcessingLeadTime),
    amazonReceivingLeadTime: num(ap.amazonReceivingLeadTime, DEFAULT_REPLENISHMENT_SETTINGS.amazonReceivingLeadTime),
    defaultTargetDays: num(ap.defaultTargetDays, DEFAULT_REPLENISHMENT_SETTINGS.defaultTargetDays),
    defaultAsvWindow: ASV_WINDOW_OPTIONS.includes(num(ap.defaultAsvWindow, 0))
      ? num(ap.defaultAsvWindow, DEFAULT_REPLENISHMENT_SETTINGS.defaultAsvWindow)
      : DEFAULT_REPLENISHMENT_SETTINGS.defaultAsvWindow,
    // Default ON — only an explicit `false` disables Pending-on-PO in the formulas.
    includePendingOnPo: ap.includePendingOnPo !== false
  };
};

/*
 * Per-SKU Target-Days override — remembers the target-coverage window a user picked for a specific
 * SKU on the Restock page, so it's pre-filled next visit. Stored as a sparse map { [sku_id]: days }
 * in the same user_active_preferences store under its own context_key (only overridden SKUs are
 * kept; the rest fall back to the account-level default target days).
 */
export const REPLENISHMENT_TARGET_DAYS_CONTEXT = 'replenishment-target-days';

export const fetchTargetDaysOverrides = createAsyncThunk(
  'restock/get-target-days',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${NODE}/users/active-preference`, {
        params: { context_key: REPLENISHMENT_TARGET_DAYS_CONTEXT }
      });
      return response?.data;
    } catch (err) {
      return handleRejectedResponse(err, 'Get Target Days Overrides', rejectWithValue);
    }
  }
);

export const saveTargetDaysOverrides = createAsyncThunk(
  'restock/save-target-days',
  async (overrides, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${NODE}/users/active-preference`, {
        context_key: REPLENISHMENT_TARGET_DAYS_CONTEXT,
        applied_filters: overrides || {}
      });
      return response?.data;
    } catch (err) {
      return handleRejectedResponse(err, 'Save Target Days Overrides', rejectWithValue);
    }
  }
);

// { [sku_id]: days } map from a GET /users/active-preference response (or {}).
export const extractTargetDaysOverrides = (payload) => {
  const ap = payload?.data?.applied_filters;
  return ap && typeof ap === 'object' && !Array.isArray(ap) ? ap : {};
};

/*
 * Per-SKU MANUAL Recommended Qty — a user-entered recommended qty stored on the SKU itself
 * (seller_skus.manual_recommended_qty). It wins over the system-computed value and drives the
 * product-level numbers (detail row + Add-to-PO modal). The value comes back on each SKU row from
 * get-skus (field `manual_recommended_qty`); saving PATCHes the single SKU. value = null clears it.
 */
export const saveManualRecommended = createAsyncThunk(
  'restock/save-manual-recommended',
  async ({ sku_id, value }, { rejectWithValue }) => {
    try {
      const response = await axios.patch(`${NODE}/skus/manual-recommended-qty`, { sku_id, value });
      return response?.data;
    } catch (err) {
      return handleRejectedResponse(err, 'Save Manual Recommended Qty', rejectWithValue);
    }
  }
);

/*
 * Add to PO — two steps:
 *  1. FetchRestockVendorItems: for the chosen vendor + selected SKUs, return each SKU's vendor
 *     item (product connected to that vendor) and its latest unit cost, to prefill the modal.
 *  2. RestockAddToPo: create one draft PO for the vendor with a line per SKU (order_quantity =
 *     recommended qty, unit_cost = edited cost) and auto-map each SKU to its line (po_sku_mappings).
 */
export const FetchRestockVendorItems = createAsyncThunk(
  'restock/vendor-items',
  async ({ vendorId, skuIds }, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${NODE}/po/restock-vendor-items`, { vendorId, skuIds });
      return response?.data; // { success, data: [{ sku_id, sku, asin, upc, vendor_item_id, unit_cost, ... }] }
    } catch (err) {
      return handleRejectedResponse(err, 'Add to PO', rejectWithValue);
    }
  }
);

// Create a new PO (pass vendorId) OR append to an existing pending PO (pass poId), with line items.
export const RestockAddToPo = createAsyncThunk(
  'restock/add-to-po',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${NODE}/po/restock-add-to-po`, payload); // { vendorId|poId, items }
      return response?.data; // { success, data: { po_id, file_name, total_items, created } }
    } catch (err) {
      return handleRejectedResponse(err, 'Add to PO', rejectWithValue);
    }
  }
);

// "Add to PO" options for the given SKU(s): the vendor items connected to the SKU(s), the distinct
// vendors they connect to (for the row-action vendor dropdown), and each vendor's pending POs.
// Accepts { sku_id } (grid detail row) or { skuIds } (modal).
export const FetchRestockSkuPoOptions = createAsyncThunk(
  'restock/sku-po-options',
  async (params, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${NODE}/po/restock-sku-po-options`, params); // { sku_id } | { skuIds }
      return response?.data; // { success, data: { vendors, vendorItems, pendingPosByVendor } }
    } catch (err) {
      return handleRejectedResponse(err, 'Add to PO', rejectWithValue);
    }
  }
);

// Per-SKU PO breakdown behind the "Pending on PO" + "WH Inbound" cells: the PO lines making up each.
export const FetchRestockSkuPoBreakdown = createAsyncThunk(
  'restock/sku-po-breakdown',
  async (params, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${NODE}/po/restock-sku-po-breakdown`, params); // { sku_id }
      return response?.data; // { success, data: { pending: [...], inbound: [...] } }
    } catch (err) {
      return handleRejectedResponse(err, 'PO Breakdown', rejectWithValue);
    }
  }
);

// Total lead time (days) = vendor + warehouse processing + amazon receiving. Single default vendor
// lead time (per the finalized decision — NOT per-vendor).
export const totalLeadTime = (settings = DEFAULT_REPLENISHMENT_SETTINGS) =>
  (Number(settings.vendorLeadTime) || 0)
  + (Number(settings.warehouseProcessingLeadTime) || 0)
  + (Number(settings.amazonReceivingLeadTime) || 0);

// Current stock = full inventory position across all buckets:
//   on-hand (warehouse_stock) + inbound (warehouse_inbound) + Amazon total (amazon_total)
//   + pending on PO (pending_on_po) + reserved stock (reserved_stock).
// reservedStock MUST be included: get-skus's amazon_total no longer includes
// inbound_working_quantity (it's subtracted there to avoid double-counting against the
// Reserved Stock column), and Internal/Draft Reserved (the Pack Now pre-placement gap) was never
// in any of the other buckets at all — omitting reservedStock here would silently under-count
// current stock and over-recommend reorders for any SKU with an open Pack Now plan or an Amazon
// inbound-working shipment.
export const computeCurrentStock = ({ onHand, inbound, amzTotal, pendingOnPo, reservedStock }) =>
  (Number(onHand) || 0) + (Number(inbound) || 0) + (Number(amzTotal) || 0) + (Number(pendingOnPo) || 0) + (Number(reservedStock) || 0);

/*
 * Recommended reorder quantity. `asv` is units/day (seller_skus.adjusted_sales_velocity).
 *
 *   current_stock   = on_hand + inbound + amz_total + pending_on_po + reserved_stock
 *   recommended_qty = max(0, round((target_days + total_lead_time) × asv − current_stock))
 *
 * Lead time is ADDED to the target-coverage window: cover demand over the target period PLUS the
 * time it takes to receive stock. Mirrors the jobs microservice's calculate-recommended-qty.js.
 */
export const computeRecommendedQty = ({ asv, targetDays, leadTimeDays, onHand, inbound, amzTotal, pendingOnPo, reservedStock }) => {
  const v = Number(asv) || 0;
  const td = Number(targetDays) || 0;
  const lt = Number(leadTimeDays) || 0;
  const currentStock = computeCurrentStock({ onHand, inbound, amzTotal, pendingOnPo, reservedStock });
  return Math.max(0, Math.round((td + lt) * v - currentStock));
};
